import { Component, OnInit, OnDestroy } from "@angular/core";
import {
  ActivatedRoute,
  Router,
  RouterModule,
  NavigationEnd,
} from "@angular/router";
import { filter } from "rxjs/operators";
import { CommonModule } from "@angular/common";
import { Subscription } from "rxjs";

interface Breadcrumb {
  label: string;
  url: string;
}

@Component({
  standalone: true,
  selector: "app-breadcrumb",
  templateUrl: "./breadcrumb.component.html",
  imports: [CommonModule, RouterModule],
  styleUrls: ["./breadcrumb.component.css"],
})
export class BreadcrumbComponent implements OnInit, OnDestroy {
  breadcrumbs: Breadcrumb[] = [];
  private routerSubscription: Subscription | null = null;

  constructor(private router: Router, private activatedRoute: ActivatedRoute) {}

  ngOnInit() {
    // Set initial breadcrumbs
    this.updateBreadcrumbs();

    // Update breadcrumbs on navigation
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateBreadcrumbs();
      });
  }

  ngOnDestroy() {
    if (this.routerSubscription) {
      this.routerSubscription.unsubscribe();
    }
  }

  private updateBreadcrumbs(): void {
    this.breadcrumbs = [];
    const root = this.activatedRoute.root;
    this.buildBreadcrumbs(root);
  }

  buildBreadcrumbs(route: ActivatedRoute, url: string = ""): void {
    // Get the children routes
    const children = route.children;

    // If no children, we've reached the end of this branch
    if (children.length === 0) {
      return;
    }

    // Process the first child
    const child = children[0];

    // Skip non-primary outlets
    if (child.outlet !== "primary") {
      this.buildBreadcrumbs(child, url);
      return;
    }

    // Get the route URL segments
    const routeURL = child.snapshot.url
      .map((segment) => segment.path)
      .join("/");

    // Build the URL
    if (routeURL !== "") {
      url += `/${routeURL}`;
    }

    // Get breadcrumb data from route
    const label = child.snapshot.data["breadcrumb"];

    // Add breadcrumb if there's a label
    if (label) {
      this.breadcrumbs.push({
        label: label,
        url: url || "/",
      });
    }

    // Continue with the child's children
    this.buildBreadcrumbs(child, url);
  }
}
